import { Link } from "react-router-dom";
import { useState } from "react";
import { MAX_MINT_QUANTITY, envString } from "../utils/config";

const SKILL_URL = envString("VITE_CODE_APE_SKILL_URL") ?? "https://code-ape.com/skill";
const CANONICAL_SKILL_URL = "https://code-ape.com/skill/RUN_INVENTORY.md";
const SKILL_VERSION = "code-ape-summoner@1.1.0";
const SKILL_VERSION_HASH = "0x6815ba53416ba06aff1932cc76b3832272bafab9bc8e066be382e32b06ba5546";
const REFERENCE_PACK_VERSION = "reference-pack-v1.0.0";
const REFERENCE_PACK_HASH = "0x03a1f937a3e01fb8ba412bede58f2a8a1412657203ba96ab98f5fd4836d4107a";
const PROMPT_QUANTITIES = [1, 3, 5, 10].filter((quantity) => quantity <= MAX_MINT_QUANTITY);

const PIPELINE = [
  {
    title: "Authorize once",
    body: "Open AI Agent Auth, connect your wallet, and sign one login message. No ETH moves.",
    linkTo: "/agent-auth",
    linkText: "Open AI Agent Auth"
  },
  {
    title: "Send the command",
    body: "Copy the full one-line command from AI Agent Auth into Codex. Compatible agents can use the same public Skill if they can operate files and call the authenticated backend flow."
  },
  {
    title: "Get a mint link",
    body: "The agent requests a pre-pinned inventory handoff, receives the signed mint pack, and returns a mint link."
  },
  {
    title: "Choose wallet and mint",
    body: "Open the link, review the Ape, choose All wallets, WalletConnect, or your browser wallet, then approve the final mint transaction."
  }
] as const;

const WALKTHROUGH = [
  "Runs the compact CODE APE inventory runbook inside Codex or another compatible agent; the website is a wallet console, not a generator.",
  "Requests a wallet-authorized pre-pinned mint pack from the backend.",
  "Receives a voucher only after the server validates the inventory item and policy fields.",
  "Stores the signed packet behind a short mint link. No raw image bytes, API keys, JWTs, or private keys belong in the link."
] as const;

const HASH_DISCLOSURES = [
  ["Skill", SKILL_VERSION, SKILL_VERSION_HASH],
  ["Reference pack", REFERENCE_PACK_VERSION, REFERENCE_PACK_HASH]
] as const;

export default function SkillPage() {
  const [selectedQuantity, setSelectedQuantity] = useState<number>(1);
  const [copiedLabel, setCopiedLabel] = useState<string | null>(null);
  const selectedPrompt = buildPrompt(selectedQuantity);

  async function copyPrompt(quantity: number) {
    const prompt = buildPrompt(quantity);
    setSelectedQuantity(quantity);
    setCopiedLabel(null);
    try {
      await navigator.clipboard?.writeText(prompt);
      setCopiedLabel(`Copied ${quantity}-Ape prompt`);
    } catch {
      setCopiedLabel("Prompt selected. Copy it manually if your browser blocks clipboard access.");
    }
  }

  function selectPrompt(quantity: number) {
    setSelectedQuantity(quantity);
    setCopiedLabel(null);
  }

  return (
    <main className="page">
      <div className="container" style={{ paddingBottom: "var(--space-24)" }}>
        <section style={{ maxWidth: 980, marginBottom: "var(--space-12)" }}>
          <div className="badge badge-prime" style={{ marginBottom: "var(--space-4)" }}>PUBLIC AI AGENT SKILL</div>
          <h1 className="text-hero" style={{ marginBottom: "var(--space-4)" }}>
            Start with Codex.<br />
            <span className="gradient-text">Stay agent-compatible.</span>
          </h1>
          <p className="text-xl text-muted" style={{ lineHeight: 1.65 }}>
            Codex is the recommended client for the smoothest summon. CODE APE is protocol-level: it verifies a public Skill, matching hashes, and a wallet-bound AI-agent handoff before the final wallet mint.
          </p>
          <p className="lede-alt">
            This page documents the compact runbook. It signs nothing, mints nothing, and performs no backend calls.
          </p>
        </section>

        <section className="card" style={{ padding: "var(--space-6)", marginBottom: "var(--space-8)" }}>
          <div className="badge badge-cyan" style={{ marginBottom: "var(--space-3)" }}>Exact prompt</div>
          <h2 className="text-2xl" style={{ marginBottom: "var(--space-3)" }}>Preview the recommended prompt</h2>
          <pre className="text-mono" translate="no" style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", color: "var(--text-primary)", background: "rgba(0,255,65,0.07)", border: "1px solid var(--color-border)", borderRadius: "var(--radius)", padding: "var(--space-4)", lineHeight: 1.7 }}>{selectedPrompt}</pre>
          <p className="text-sm text-muted" style={{ marginTop: "var(--space-3)", lineHeight: 1.7 }}>
            Choose a quantity up to {MAX_MINT_QUANTITY}. For the smoothest run, authorize first and paste the full one-line command from AI Agent Auth. If this preview prompt has no temporary pass, the Skill will stop and ask for auth before requesting a mint pack.
          </p>
          <div style={{ display: "flex", gap: "var(--space-3)", flexWrap: "wrap", marginTop: "var(--space-4)" }}>
            {PROMPT_QUANTITIES.map((quantity) => (
              <button
                key={quantity}
                className={selectedQuantity === quantity ? "btn btn-primary" : "btn btn-secondary"}
                type="button"
                onMouseEnter={() => selectPrompt(quantity)}
                onFocus={() => selectPrompt(quantity)}
                onClick={() => copyPrompt(quantity)}
              >
                Copy {quantity}-Ape Prompt
              </button>
            ))}
            <Link className="btn btn-primary" to="/agent-auth">Authorize First</Link>
            <Link className="btn btn-secondary" to="/mint">Open Mint Page</Link>
            <Link className="btn btn-secondary" to="/manifesto">Read Manifesto</Link>
          </div>
          {copiedLabel && <p className="text-sm" style={{ marginTop: "var(--space-3)", color: "var(--color-prime)", fontWeight: 700 }}>{copiedLabel}</p>}
        </section>

        <section className="skill-pipeline" style={{ marginBottom: "var(--space-8)" }}>
          <h2 className="text-3xl skill-pipeline__title" style={{ marginBottom: "var(--space-5)" }}>The mint flow users actually run</h2>
          <ol className="skill-pipeline__grid" style={{ listStyle: "none" }}>
            {PIPELINE.map((step, index) => (
              <li className="card skill-step-card" key={step.title}>
                <div className="badge badge-prime" style={{ marginBottom: "var(--space-3)" }}>{index + 1}</div>
                <h3 className="text-xl" style={{ marginBottom: "var(--space-2)" }}>{step.title}</h3>
                <p className="text-muted" style={{ lineHeight: 1.65 }}>
                  {step.body}
                  {"linkTo" in step && (
                    <>
                      {" "}
                      <Link to={step.linkTo} style={{ color: "var(--color-prime)", fontWeight: 700 }}>
                        {step.linkText}
                      </Link>
                    </>
                  )}
                </p>
              </li>
            ))}
          </ol>
        </section>

        <section className="grid-2" style={{ alignItems: "start", marginBottom: "var(--space-8)" }}>
          <div className="card" style={{ padding: "var(--space-5)" }}>
            <div className="badge badge-gold" style={{ marginBottom: "var(--space-3)" }}>Provenance hashes</div>
            <h2 className="text-2xl" style={{ marginBottom: "var(--space-4)" }}>What every handoff shows</h2>
            <div style={{ display: "grid", gap: "var(--space-3)" }}>
              {HASH_DISCLOSURES.map(([label, version, hash]) => (
                <div key={label} style={{ border: "1px solid var(--color-border)", borderRadius: "var(--radius)", padding: "var(--space-3)", background: "rgba(255,255,255,0.025)" }}>
                  <div className="text-sm text-muted">{label}</div>
                  <strong className="text-mono">{version}</strong>
                  <code className="text-xs" style={{ display: "block", marginTop: "var(--space-2)", color: "var(--color-prime)", overflowWrap: "anywhere" }}>{hash}</code>
                </div>
              ))}
            </div>
          </div>

          <div className="alert alert-info" style={{ alignItems: "flex-start" }}>
            <span>✓</span>
            <div>
              <strong>Official reference pack</strong>
              <p style={{ marginTop: "var(--space-2)", lineHeight: 1.7 }}>
                The current reference pack hash is reproducible and published for the live CODE APE launch. The archive lets users and compatible agents confirm the same creative reference set.
              </p>
            </div>
          </div>
        </section>

        <section className="card" style={{ padding: "var(--space-6)", marginBottom: "var(--space-8)" }}>
          <div className="badge badge-cyan" style={{ marginBottom: "var(--space-3)" }}>Client compatibility</div>
          <h2 className="text-3xl" style={{ marginBottom: "var(--space-3)" }}>Recommended client: Codex</h2>
          <p className="text-muted" style={{ lineHeight: 1.7, marginBottom: "var(--space-4)" }}>
            Codex is the default path because it is smooth today. Compatible clients include Claude Code, Cursor, Cline, and custom agents when they can run this public Skill, call the authenticated backend flow, request a pre-pinned mint pack, and create a canonical handoff.
          </p>
          <p className="text-sm text-muted" style={{ lineHeight: 1.7 }}>
            Pure chatbots that cannot operate files or call backend routes can discuss CODE APE, but they cannot complete the summon by themselves.
          </p>
        </section>

        <section className="card" style={{ padding: "var(--space-6)" }}>
          <h2 className="text-3xl" style={{ marginBottom: "var(--space-5)" }}>What the agent actually does</h2>
          <ul style={{ listStyle: "none", display: "grid", gap: "var(--space-3)" }}>
            {WALKTHROUGH.map((item) => (
              <li key={item} className="text-muted" style={{ display: "grid", gridTemplateColumns: "28px 1fr", gap: "var(--space-3)", lineHeight: 1.65 }}>
                <span style={{ color: "var(--color-prime)" }}>→</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <p className="text-sm text-muted" style={{ marginTop: "var(--space-5)", lineHeight: 1.65 }}>
            Safety note: this page is a public guide. It performs no backend calls and cannot mint. Minting starts only after Codex or another compatible agent returns a valid short mint link and ends with your wallet transaction in <Link to="/mint" style={{ color: "var(--color-prime)" }}>/mint</Link>.
          </p>
        </section>
      </div>
    </main>
  );
}

function buildPrompt(quantity: number): string {
  return quantity <= 1
    ? `Codex, read ${CANONICAL_SKILL_URL} and prepare my CODE APE mint. Do not generate images; request the inventory handoff and return the mintUrl first.`
    : `Codex, read ${CANONICAL_SKILL_URL} and prepare my ${quantity}-Ape CODE APE mint. Do not generate images; request the inventory handoff and return the mintUrl first.`;
}
